import { DataGridPro } from '@mui/x-data-grid-pro';
import * as React from 'react';
import { EmptyReturnAvailableResponse, SmartScreenshotResponse } from 'services/clients/perDiem';
import { Permission, useHasPermission } from 'utils/permissions';
import { useMediaQuery } from '@mui/material';
import mediaQueries from 'mediaqueries';
import { AlertRow, FightableRow, NoDataRow, PlaceholderRow, SmartRow } from './types';
import { getColumns } from './columns';
import { shouldDisplayToolbar, TableToolbar } from './TableToolbar';

interface Props {
  data?: EmptyReturnAvailableResponse;
  loading?: boolean;
  onModal: (code: SmartScreenshotResponse['code']) => void;
  pageSize: number;
  page: number;
  setPageSize: (pageSize: number) => void;
  setPage: (page: number) => void;
  variant?: 'minimal';
}

export const PerDiemDisputeTable: React.FC<Props> = (props) => {
  const { data, loading, onModal, pageSize, page, setPageSize, setPage, variant } = props;
  const canAccessPerDiem = useHasPermission([Permission.PerDiemSmartPerDiemDownload]);

  const columns = React.useMemo(
    () => getColumns(onModal, canAccessPerDiem, variant),
    [onModal, canAccessPerDiem, variant]
  );

  const rows: SmartRow[] = React.useMemo(() => {
    if (!data || loading) {
      const placeholder: PlaceholderRow = {
        type: 'placeholder',
        id: '1',
      };

      return Array(10)
        .fill(placeholder)
        .map((p, i) => ({ ...p, id: i.toString() }));
    }

    return data.results.map((row) => {
      if (row.isFightable === null) {
        const noData: NoDataRow = {
          type: 'no-data',
          id: row.id,
          date: row.datetime,
          shift: row.shift,
          screenshotTimestamp: row.screenshotTimestamp || '',
        };
        return noData;
      }

      if (row.isFightable) {
        const fightable: FightableRow = {
          type: 'normal',
          fightable: true,
          id: row.id,
          date: row.datetime,
          shift: row.shift,
          terminalRestrictions: row.reasonString || '',
          screenshotTimestamp: row.screenshotTimestamp || '',
        };
        return fightable;
      }

      const alert: AlertRow = {
        type: 'alert',
        id: row.id,
        date: row.datetime,
        fightable: false,
        shift: row.shift,
        screenshotTimestamp: row.screenshotTimestamp || '',
      };
      return alert;
    });
  }, [data, loading]);

  const isTabletOrSmaller = useMediaQuery(`(max-width: ${mediaQueries.lg})`);

  const displayToolbar = React.useMemo(() => {
    return shouldDisplayToolbar(data?.portCollection?.smartPerDiemMinDate, data?.startDate);
  }, [data]);

  return (
    <DataGridPro
      autoHeight
      rowCount={data?.count}
      rows={rows}
      columns={columns}
      disableSelectionOnClick
      checkboxSelection={false}
      pagination
      paginationMode="server"
      onPageSizeChange={setPageSize}
      pageSize={pageSize}
      onPageChange={setPage}
      page={page}
      className="w-full h-full"
      rowHeight={isTabletOrSmaller ? 124 : 62}
      disableColumnReorder
      components={{
        Toolbar: displayToolbar ? TableToolbar : undefined,
      }}
      componentsProps={{
        pagination: {
          SelectProps: {
            MenuProps: {
              disablePortal: true,
            },
          },
        },
        toolbar: {
          portCollection: data?.portCollection,
        },
      }}
      getRowClassName={(params) => {
        switch (params.row.type) {
          case 'alert':
            return 'bg-red-50';
          case 'no-data':
            return 'bg-gray-50';
          default:
            return '';
        }
      }}
      isRowSelectable={(params) => {
        switch (params.row.type) {
          case 'alert':
          case 'no-data':
          case 'placeholder':
            return false;
          default:
            return true;
        }
      }}
      classes={{
        cellCheckbox: 'flex items-center justify-center',
        columnHeader: 'bg-white border-none',
        columnHeaders: 'shadow-sm border-b border-blue-400',
        columnHeaderCheckbox: 'bg-white shadow-sm py-4',
        root: 'bg-white border-none',
        main: 'bg-white',
        footerContainer: 'flex flex-row-reverse flex-grow border-none bg-white',
        checkboxInput: 'bg-transparent',
        virtualScroller: displayToolbar ? 'pt-6' : '',
      }}
      sx={{
        '.MuiDataGrid-cellCheckbox': {
          background: 'transparent',
        },
        '.MuiDataGrid-cellCheckbox .Mui-disabled': {
          display: 'none',
        },
        '.MuiDataGrid-cell:nth-of-type(1)': {
          borderRight: 'none',
        },
        '.MuiDataGrid-columnSeparator': {
          display: 'none',
        },
      }}
      disableColumnMenu
    />
  );
};
PerDiemDisputeTable.displayName = 'PerDiemDisputeTable';
